import { Refresh as RefreshIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { useContext, useEffect, useState } from "react";
import { LABEL_ISSUE_LABELS, LabelIssueType } from "types/ShippingTypes";
import { apiGetLabelIssues, apiMarkLabelIssueOk } from "util/network/Shipping";
import {
  AppLink,
  SeverityChip,
  ShipmentPage,
  errorText,
  formatDateTime,
  useIsManager,
} from "./common";

type Filter = "open" | "ok" | "cleared" | "all";

const FILTER_LABELS: Record<Filter, string> = {
  open: "Open",
  ok: "Marked OK",
  cleared: "Cleared",
  all: "All",
};

/** Labels the checks flagged. An issue clears by itself when the cause goes
 *  away; a manager can mark the rest OK with a note. */
const LabelIssues: React.FC = () => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const isManager = useIsManager();
  const [filter, setFilter] = useState<Filter>("open");
  const [issues, setIssues] = useState<LabelIssueType[]>([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState<LabelIssueType | null>(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    apiGetLabelIssues(token, filter)
      .then(setIssues)
      .catch((error) => addToast({ type: "error", message: errorText(error) }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (token) load();
  }, [token, filter]);

  const markOk = () => {
    if (!target || !note.trim()) return;
    setSaving(true);
    apiMarkLabelIssueOk(token, target.id, note.trim())
      .then((updated) => {
        setIssues((rows) =>
          filter === "open"
            ? rows.filter((row) => row.id !== updated.id)
            : rows.map((row) => (row.id === updated.id ? updated : row))
        );
        setTarget(null);
        addToast({ type: "success", message: "Marked OK" });
      })
      .catch((error) => addToast({ type: "error", message: errorText(error) }))
      .finally(() => setSaving(false));
  };

  return (
    <ShipmentPage
      title="Label issues"
      actions={
        <Tooltip title="Refresh">
          <IconButton onClick={load} sx={{ color: "#fff" }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      }
    >
      <Tabs
        value={filter}
        onChange={(_, value) => setFilter(value)}
        sx={{ mb: 2 }}
      >
        {(Object.keys(FILTER_LABELS) as Filter[]).map((value) => (
          <Tab key={value} value={value} label={FILTER_LABELS[value]} />
        ))}
      </Tabs>
      {loading ? (
        <CircularProgress />
      ) : issues.length === 0 ? (
        <Typography color="text.secondary">No issues.</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Severity</TableCell>
                <TableCell>Issue</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Raised</TableCell>
                <TableCell>State</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {issues.map((issue) => (
                <TableRow key={issue.id}>
                  <TableCell>
                    <SeverityChip severity={issue.severity} />
                  </TableCell>
                  <TableCell>
                    {LABEL_ISSUE_LABELS[issue.type] ?? issue.type}
                    {issue.detail && (
                      <Typography variant="caption" display="block">
                        {issue.detail}
                      </Typography>
                    )}
                    {issue.note && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        Note: {issue.note}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {issue.orderId ? (
                      <AppLink to={`/shipment/packing/${issue.orderId}`}>
                        {issue.orderNo}
                      </AppLink>
                    ) : (
                      <AppLink to="/shipment/unmatched">Unmatched</AppLink>
                    )}
                  </TableCell>
                  <TableCell>{issue.trackingNo ?? issue.shipmondoId}</TableCell>
                  <TableCell>{formatDateTime(issue.created)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        FILTER_LABELS[issue.state as Filter] ?? issue.state
                      }
                    />
                    {issue.okAt && (
                      <Typography variant="caption" display="block">
                        {formatDateTime(issue.okAt)}
                      </Typography>
                    )}
                    {issue.clearedAt && (
                      <Typography variant="caption" display="block">
                        {formatDateTime(issue.clearedAt)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {issue.state === "open" && isManager && (
                      <Button
                        size="small"
                        onClick={() => {
                          setNote("");
                          setTarget(issue);
                        }}
                      >
                        Mark OK
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={!!target}
        onClose={() => setTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mark OK</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 1 }}>
            {target && (LABEL_ISSUE_LABELS[target.type] ?? target.type)}
            {target?.orderNo ? ` - order ${target.orderNo}` : ""}
          </Box>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Why is this OK?"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTarget(null)}>Close</Button>
          <Button
            variant="contained"
            disabled={!note.trim() || saving}
            onClick={markOk}
          >
            Mark OK
          </Button>
        </DialogActions>
      </Dialog>
    </ShipmentPage>
  );
};

export default LabelIssues;
